import type { SDKState } from "@/state/sdk-state";
import type { ApiService } from "../api/api";
import type { ToastNotificationManager } from "@/ui/toast-notification-manager";
import * as z from "zod/v4-mini";
import { joinURL } from "@/utils/url";
import { AuthIframeManager } from "./auth-iframe-manager";
import { AsyncFlowMessageType } from "@f3/shared/async-flow-messages";
import { AuthModalManager } from "./auth-modal-manager";
import { Logger } from "@/utils/logger";
import { GqlAuthChannelType } from "@/state/sdk-auth-state";
import { IntegrationMode } from "@/state/sdk-channel-info-state";

const InitiateAuthenticationOutputSchema = z.object({
  code: z.string(),
  state: z.string(),
  success: z.boolean(),
});

const UserPublicSchema = z.object({
  id: z.string(),
  email: z.email(),
  username: z.string(),
});

const ValidatedUserIdentifierSchema = z.object({
  type: z.string(),
  value: z.string(),
  confirmation_token: z.string(),
});

const ValidateAuthenticationOutputSchema = z.object({
  success: z.boolean(),
  data: z.nullable(
    z.object({
      state: z.string(),
      user: z.nullable(UserPublicSchema),
      auth_channel: z.enum(GqlAuthChannelType),
      validated_identifiers: z.nullable(z.array(ValidatedUserIdentifierSchema)),
    }),
  ),
});

export class AuthService {
  #state: SDKState;
  #api: ApiService;
  #authIframeManager: AuthIframeManager;
  #authModalManager: AuthModalManager;
  #logger: Logger;
  #toastNotificationManager: ToastNotificationManager;

  constructor(
    state: SDKState,
    api: ApiService,
    logger: Logger,
    toastNotificationManager: ToastNotificationManager,
  ) {
    this.#state = state;
    this.#api = api;
    this.#logger = logger.extend(this.constructor.name);
    this.#toastNotificationManager = toastNotificationManager;
    this.#authIframeManager = new AuthIframeManager(state, this.#logger);
    this.#authModalManager = new AuthModalManager(this.#logger);
  }

  init() {
    this.#warnOnNonFanfestOrigin();
    this.#authIframeManager.init();
    this.#authIframeManager.broker.authObservable.subscribe(
      async ({ data }) => {
        if (data.type === AsyncFlowMessageType.ValidateAuthenticationSuccess) {
          await this.validateAuthentication(
            data.code,
            data.state,
            data.redirect_uri,
          );
          // Detach the authentication iframe
          await this.#authIframeManager.detachSilentIframe();
        }

        if (data.type === AsyncFlowMessageType.AuthModalSuccess) {
          await this.validateFanfestAuth(data.sessionToken, data.authChannel);
          this.#authModalManager.close();
        }

        if (data.type === AsyncFlowMessageType.AuthModalClosed) {
          this.#authModalManager.close();
        }
      },
    );
  }

  #warnOnNonFanfestOrigin() {
    const appOrigin = this.#state.options.appOrigin;
    if (
      appOrigin !== "https://fanfest.vip" &&
      !appOrigin.endsWith(".fanfest.vip")
    ) {
      this.#logger.warn(
        `appOrigin is set to a non-FanFest domain: ${appOrigin}. ` +
          "Ensure this is intentional (e.g., staging environment).",
      );
    }

    const authModalUrl = this.#state.channelInfo.authModalUrl;
    if (authModalUrl) {
      const modalDomain = new URL(authModalUrl).origin;
      if (modalDomain !== appOrigin) {
        this.#logger.error(
          `auth_modal_url domain (${modalDomain}) does not match appOrigin (${appOrigin}). ` +
            "This is a security misconfiguration. Auth modal will not work correctly.",
        );
      }
    }
  }

  detach() {
    this.#state.authState.isAuthenticating.value = false;
    this.#authIframeManager.detach();
  }

  openAuthModal() {
    const url = this.#state.channelInfo.authModalUrl;
    if (!url) {
      this.#logger.error(
        "Cannot open auth modal: auth_modal_url not available",
      );
      return;
    }
    const fullUrl = new URL(url);
    fullUrl.searchParams.set("app_id", this.#state.clientId);
    this.#authModalManager.open(fullUrl.toString());
  }

  async initiateAuthentication() {
    if (this.#state.authState.isAuthenticated) {
      return;
    }

    const integrationMode = this.#state.channelInfo.integrationMode;

    if (integrationMode === IntegrationMode.FanfestAuth) {
      this.openAuthModal();
    } else {
      await this.#initiateSilentAuthentication();
    }
  }

  async #initiateSilentAuthentication() {
    if (this.#state.authState.isAuthenticated) {
      return;
    }

    this.#state.authState.isAuthenticating.value = true;
    const data = await this.#api.POST(`/auth/silent-initiate`, {
      redirectUri: joinURL(this.#state.options.appOrigin, `auth/callback`),
    });

    const parsed = InitiateAuthenticationOutputSchema.safeParse(data);

    if (!parsed.success) {
      this.#logger.error(
        "Failed to parse initiate authentication response",
        parsed.error.message,
      );
      return;
    }

    this.#logger.info("Initiating silent authentication");

    await this.#authIframeManager.createSilentIframe(parsed.data.code);
  }

  async validateFanfestAuth(
    sessionToken: string,
    authChannel: "otp" | "oauth",
  ) {
    const data = await this.#api.POST("/auth/fanfest-auth-validate", {
      sessionToken,
      authChannel,
    });

    const parsed = ValidateAuthenticationOutputSchema.safeParse(data);

    if (!parsed.success) {
      this.#logger.error(
        "Failed to parse FanFest Auth validation response",
        parsed.error.message,
      );
      this.#state.authState.isAuthenticating.value = false;
      return;
    }

    this.#logger.info("Validated FanFest authentication");

    if (parsed.data.data) {
      this.#state.authState.value.set({
        authToken: sessionToken,
        user: parsed.data.data.user ?? null,
        validatedIdentifiers: parsed.data.data.validated_identifiers ?? [],
        authChannelType: parsed.data.data.auth_channel,
      });

      if (parsed.data.data.user) {
        this.#toastNotificationManager.show({
          message: `Welcome, ${parsed.data.data.user.username}! Your rewards are being tracked.`,
          actionLabel: "View Rewards",
          actionCallback: () => this.#state.options.hostRewardsFn?.(),
          duration: 5000,
        });
      }
    } else {
      this.#state.authState.value.remove();
    }
    this.#state.authState.isAuthenticating.value = false;
  }

  async validateAuthentication(
    code: string,
    state: string,
    redirectUri: string,
  ) {
    const data = await this.#api.POST(`/auth/silent-validation`, {
      code,
      state,
      redirectUri,
    });

    const parsed = ValidateAuthenticationOutputSchema.safeParse(data);

    if (!parsed.success) {
      this.#logger.error(
        "Failed to parse validate authentication response",
        parsed.error.message,
      );
      return;
    }

    this.#logger.info("Validated authentication");

    if (parsed.data.data) {
      this.#state.authState.value.set({
        authToken: parsed.data.data.state,
        user: parsed.data.data.user ?? null,
        validatedIdentifiers: parsed.data.data.validated_identifiers ?? [],
        authChannelType: parsed.data.data.auth_channel,
      });
    } else {
      this.#state.authState.value.remove();
    }
    this.#state.authState.isAuthenticating.value = false;
  }
}
